import React from 'react';
import { motion } from 'framer-motion';
import { Search, Calendar, User, ChevronRight, Tag } from 'lucide-react';
import { Link } from 'react-router-dom';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}
const blogPosts = [{
  id: 1,
  title: 'Transitioning to Assisted Living: How to Have Conversations with Aging Loved Ones',
  date: 'May 19, 2023',
  categories: ['Elderly Care', 'Health', 'Professionals'],
  views: 914,
  excerpt: 'Learn how to navigate sensitive conversations with aging loved ones about transitioning to assisted living. Discover effective strategies for approaching the topic with empathy and understanding, addressing concerns and fears, and involving them in the decision-making process.',
  image: 'https://elisabethhouse.net/wp-content/uploads/2023/05/pexels-kampus-production-7551583-scaled.jpg'
}, {
  id: 2,
  title: "Assisted Living vs. Nursing Home: What's the Difference? | A Guide by Elisabeth's House",
  date: 'December 1, 2025',
  categories: ['Uncategorized'],
  views: 122,
  excerpt: 'Choosing the right care environment for a loved one is rarely easy. Families often find themselves standing between assisted living and nursing home care, unsure of the difference and even less sure of which option is the right fit.',
  image: 'https://elisabethhouse.net/wp-content/uploads/2025/10/Untitled-9.791-x-8.208-in-1200-x-628-px-390x405.png'
}, {
  id: 3,
  title: 'When Love Means Letting Go: The Truth About Moving a Parent to Assisted Living',
  date: 'October 16, 2025',
  categories: ['Uncategorized'],
  views: 200,
  excerpt: 'There comes a time in many families when love demands a hard decision, one that feels both heartbreaking and necessary. For many sons and daughters, that moment is realizing that their aging parents can no longer live safely at home.',
  image: 'https://images.unsplash.com/photo-1516733725897-1aa73b87c8e8?auto=format&fit=crop&q=80&w=800' // Placeholder
}, {
  id: 4,
  title: "Living with Parkinson's: What Seniors and Families Should Know",
  date: 'August 28, 2025',
  categories: ['Elderly Care'],
  views: 310,
  excerpt: "At Elisabeth's House, we understand that getting older can bring new challenges. For some seniors, one of those challenges is Parkinson's disease. While the diagnosis can feel overwhelming at first, it doesn't mean life has to stop.",
  image: 'https://elisabethhouse.net/wp-content/uploads/2025/08/istockphoto-1778181453-612x612-1-390x405.jpg'
}, {
  id: 5,
  title: 'Recognizing Signs Your Loved One Might Need Assisted Living',
  date: 'April 10, 2024',
  categories: ['Company News', 'Elderly Care', 'Health', 'Medical Treatments', 'Professionals'],
  views: 1651,
  excerpt: 'Deciding to move a loved one into an assisted living facility can be difficult. As families, we want our loved ones to be able to live independently for as long as possible. However, there often comes a time when safety and well-being become primary concerns.',
  image: 'https://images.unsplash.com/photo-1531983412531-1f49a365ffed?auto=format&fit=crop&q=80&w=800' // Placeholder
}, {
  id: 6,
  title: 'Shedding Light on Diabetic Eye Disease: A Call for Awareness',
  date: 'December 5, 2023',
  categories: ['Elderly Care', 'Health', 'Professionals'],
  views: 923,
  excerpt: 'Topics covered: Diabetic eye disease, diabetic retinopathy, diabetic macular edema, cataracts, glaucoma, risk factors (high cholesterol, high blood pressure, tobacco use, pregnancy, not managing blood sugar well, having long-term diabetes).',
  image: 'https://elisabethhouse.net/wp-content/uploads/2023/12/Diabetic-Eye-Disease-390x405.jpg'
}, {
  id: 7,
  title: 'Assisted Living Unveiled: Busting Common Myths and Misconceptions',
  date: 'October 24, 2023',
  categories: ['Elderly Care', 'Health'],
  views: 829,
  excerpt: 'Topics covered: Unveiling Assisted Living: Beyond Shelter, A Community; Myth-Busting: Independence Is Not Compromised; The Truth About Healthcare; Finances Clarified; Personalizing Your Choice.',
  image: 'https://elisabethhouse.net/wp-content/uploads/2020/04/1-390x405.png'
}, {
  id: 8,
  title: 'Nurturing Homes and Hearts: The Essence of Elisabeth House And Assisted Living',
  date: 'August 25, 2023',
  categories: ['Elderly Care', 'Health'],
  views: 1234,
  excerpt: 'Topics covered: Creating a home away from home, Unparalleled personalized care, A day of enrichment, Compassion in every corner, Empowering Families, Safety and wellness, A future of care and Innovation.',
  image: 'https://elisabethhouse.net/wp-content/uploads/2023/08/Blog-banner-390x405.webp'
}, {
  id: 9,
  title: 'Assisted Living vs. Home Care: Making the Right Choice for Your Loved Ones',
  date: 'July 26, 2023',
  categories: ['Elderly Care', 'Health', 'Medical Treatments'],
  views: 990,
  excerpt: 'When it comes to ensuring the well-being and happiness of our loved ones as they age, making the right care decision is crucial. Two popular options that often come up for consideration are assisted living and home care.',
  image: 'https://elisabethhouse.net/wp-content/uploads/2023/07/MicrosoftTeams-image-49-390x405.jpg'
}];
const categories = ['Company News', 'Elderly Care', 'Health', 'Medical Treatments', 'Professionals', 'Uncategorized'];
const tags = [{
  name: 'about',
  count: 4
}, {
  name: 'care',
  count: 7
}, {
  name: 'companionship',
  count: 1
}, {
  name: 'healthy',
  count: 1
}, {
  name: 'medical',
  count: 3
}, {
  name: 'medical center',
  count: 1
}, {
  name: 'tips',
  count: 3
}];
export const Blog = () => {
  return <div className="min-h-screen bg-warmGray/5" data-id="element-290">
      {/* Page Header */}
      <section className="relative h-[50vh] min-h-[400px] flex items-center overflow-hidden pt-24" data-id="element-291">
        <div className="absolute inset-0 z-0" data-id="element-292">
          <img src="https://images.unsplash.com/photo-1516733725897-1aa73b87c8e8?auto=format&fit=crop&q=80&w=2000" alt="Elisabeth House Blog" className="w-full h-full object-cover" data-id="element-293" />
          <div className="absolute inset-0 bg-gradient-to-r from-charcoal/80 via-charcoal/50 to-charcoal/20" data-id="element-294" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 w-full" data-id="element-295">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          ease: 'easeOut'
        }} data-id="element-296">
            <div className="flex items-center space-x-3 text-white/60 text-xs uppercase tracking-[0.3em] mb-8" data-id="element-297">
              <a href="/" className="hover:text-warmCoral transition-colors" data-id="element-298">
                Home
              </a>
              <span className="w-1 h-1 bg-warmCoral rounded-full" data-id="element-299" />
              <span className="text-white" data-id="element-300">Blog</span>
            </div>

            <div className="relative inline-block" data-id="element-301">
              <h1 className="text-white text-5xl md:text-7xl font-serif leading-none mb-4" data-id="element-302">
                Our Blog
              </h1>
              <motion.span initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: 0.5,
              duration: 0.8
            }} className="absolute -top-16 -right-8 text-warmCoral font-accent text-4xl md:text-5xl rotate-[-12deg] hidden md:block" data-id="element-303">
                Latest News
              </motion.span>
            </div>

            <p className="text-white/80 text-xl md:text-2xl max-w-2xl font-light leading-relaxed mt-8 border-l-2 border-warmCoral pl-8" data-id="element-304">
              Insights, advice, and stories from our community of care
              professionals and residents.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-24 px-4 md:px-8" data-id="element-305">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-3 gap-12" data-id="element-306">
          {/* Blog Posts Grid */}
          <div className="lg:col-span-2 space-y-12" data-id="element-307">
            {blogPosts.map((post, i) => <motion.article key={post.id} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="bg-white rounded-3xl overflow-hidden shadow-sm border border-warmGray/10 hover:shadow-xl transition-all duration-500 group" data-id="element-308">
                <div className="aspect-[16/9] md:aspect-[2/1] overflow-hidden relative" data-id="element-309">
                  <img src={post.image} alt={post.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-310" />
                  <div className="absolute top-4 left-4 flex flex-wrap gap-2" data-id="element-311">
                    {post.categories.slice(0, 2).map(cat => <span key={cat} className="bg-white/90 backdrop-blur-sm text-charcoal text-xs font-medium px-3 py-1 rounded-full" data-id="element-312">
                        {cat}
                      </span>)}
                  </div>
                </div>

                <div className="p-8 md:p-10" data-id="element-313">
                  <div className="flex items-center space-x-6 text-sm text-charcoal/50 mb-6" data-id="element-314">
                    <div className="flex items-center space-x-2" data-id="element-315">
                      <Calendar className="w-4 h-4" data-id="element-316" />
                      <span data-id="element-317">{post.date}</span>
                    </div>
                    <div className="flex items-center space-x-2" data-id="element-318">
                      <User className="w-4 h-4" data-id="element-319" />
                      <span data-id="element-320">Admin</span>
                    </div>
                  </div>

                  <h2 className="text-2xl md:text-3xl font-serif text-charcoal mb-4 group-hover:text-warmCoral transition-colors" data-id="element-321">
                    <Link to={`/blog/${post.id}`} data-id="element-322">{post.title}</Link>
                  </h2>

                  <p className="text-charcoal/60 leading-relaxed mb-8" data-id="element-323">
                    {post.excerpt}
                  </p>

                  <Link to={`/blog/${post.id}`} className="inline-flex items-center space-x-2 text-lightBlue font-medium hover:text-warmCoral transition-colors" data-id="element-324">
                    <span data-id="element-325">Read More</span>
                    <ChevronRight className="w-4 h-4" data-id="element-326" />
                  </Link>
                </div>
              </motion.article>)}

            {/* Pagination */}
            <div className="flex justify-center space-x-2 pt-8" data-id="element-327">
              <button className="w-10 h-10 rounded-full bg-warmCoral text-white flex items-center justify-center font-medium" data-id="element-328">
                1
              </button>
              <button className="w-10 h-10 rounded-full bg-white text-charcoal hover:bg-warmGray/10 flex items-center justify-center font-medium transition-colors" data-id="element-329">
                2
              </button>
              <button className="w-10 h-10 rounded-full bg-white text-charcoal hover:bg-warmGray/10 flex items-center justify-center font-medium transition-colors" data-id="element-330">
                <ChevronRight className="w-5 h-5" data-id="element-331" />
              </button>
            </div>
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-1" data-id="element-332">
            <div className="space-y-10 lg:sticky lg:top-28" data-id="element-333">
              {/* Search Widget */}
              <div className="bg-white p-8 rounded-3xl shadow-sm border border-warmGray/10" data-id="element-334">
                <h3 className="text-xl font-serif text-charcoal mb-6" data-id="element-335">
                  Search
                </h3>
                <div className="relative" data-id="element-336">
                  <input type="text" placeholder="Search articles..." className="w-full bg-warmGray/5 border border-warmGray/20 rounded-xl py-3 pl-4 pr-12 text-charcoal focus:outline-none focus:border-lightBlue transition-colors" data-id="element-337" />
                  <button className="absolute right-3 top-1/2 -translate-y-1/2 text-charcoal/40 hover:text-lightBlue transition-colors" data-id="element-338">
                    <Search className="w-5 h-5" data-id="element-339" />
                  </button>
                </div>
              </div>

              {/* Categories Widget */}
              <div className="bg-white p-8 rounded-3xl shadow-sm border border-warmGray/10" data-id="element-340">
                <h3 className="text-xl font-serif text-charcoal mb-6" data-id="element-341">
                  Categories
                </h3>
                <ul className="space-y-3" data-id="element-342">
                  {categories.map(category => <li key={category} data-id="element-343">
                      <a href="#" className="flex items-center justify-between group" data-id="element-344">
                        <span className="text-charcoal/70 group-hover:text-warmCoral transition-colors" data-id="element-345">
                          {category}
                        </span>
                        <ChevronRight className="w-4 h-4 text-charcoal/30 group-hover:text-warmCoral transition-colors" data-id="element-346" />
                      </a>
                    </li>)}
                </ul>
              </div>

              {/* Tags Widget */}
              <div className="bg-white p-8 rounded-3xl shadow-sm border border-warmGray/10" data-id="element-347">
                <h3 className="text-xl font-serif text-charcoal mb-6" data-id="element-348">Tags</h3>
                <div className="flex flex-wrap gap-2" data-id="element-349">
                  {tags.map(tag => <a key={tag.name} href="#" className="inline-flex items-center space-x-1 bg-warmGray/5 hover:bg-lightBlue/10 text-charcoal/70 hover:text-lightBlue px-4 py-2 rounded-full text-sm transition-colors border border-warmGray/10" data-id="element-350">
                      <Tag className="w-3 h-3" data-id="element-351" />
                      <span data-id="element-352">{tag.name}</span>
                      <span className="text-charcoal/40 text-xs ml-1" data-id="element-353">
                        ({tag.count})
                      </span>
                    </a>)}
                </div>
              </div>

              {/* Promo Widget */}
              <div className="bg-charcoal p-8 rounded-3xl shadow-sm relative overflow-hidden" data-id="element-354">
                <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-warmCoral/20 rounded-full blur-2xl" data-id="element-355" />
                <div className="relative z-10" data-id="element-356">
                  <h3 className="text-2xl font-serif text-white mb-4" data-id="element-357">
                    Need Assistance?
                  </h3>
                  <p className="text-white/70 mb-6" data-id="element-358">
                    Our care coordinators are ready to help you find the right
                    solution for your loved one.
                  </p>
                  <button className="w-full bg-warmCoral hover:bg-white hover:text-charcoal text-white py-3 rounded-xl font-medium transition-all" data-id="element-359">
                    Contact Us Today
                  </button>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>;
};